/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { type JSX } from "react"
import { useAppSelector } from "../../app/hooks"
import { accentCleanse, nonAlphabetLanguages } from "@/lib/utils"

type WordCardProps = {
  wordIndex: number
  ref: React.RefObject<HTMLDivElement | null>
}

export const Word: React.FC<WordCardProps> = ({ wordIndex, ref }): JSX.Element | null => {
  const homeSlice = useAppSelector(state => state.home)

  //   const correctColor = "text-green-700"
  const correctColor = "text-yellow-400"
  const wrongColor = "text-red-700"
  const defaultColor = "text-white"
  const inactiveDefaultColor = "text-gray-400"

  //   const wordsBackgroundColor = "bg-gray-800"
  //   const cursorColor = "bg-amber-700"

  const conditionalWordBase = "absolute bg-gray-600 p-1 z-10 rounded-md"
  const cursorBase = "h-10 absolute leading-loose rounded animate-caret-blink bg-amber-700" // TODO: rounded size still tbd
  const cursorBaseNative = "absolute rounded animate-caret-blink bg-amber-700" // TODO: rounded size still tbd

  const showNativeWord = wordIndex === homeSlice.wordIndex

  const nativeNotAlphabet = nonAlphabetLanguages.includes(homeSlice.nativeLanguage)

  const nonAlphabetAndNonEasyMode = nonAlphabetLanguages.includes(homeSlice.foreignLanguage) && homeSlice.strictMode

  function decideAnimation(fast = false) {
    if (!nonAlphabetAndNonEasyMode) return ""
    if (!homeSlice.strictMode) return ""
    // if (homeSlice.gameState !== "running") return "opacity-0"
    return fast ? "fade-in-fast" : "fade-in"
  }

  function decideLetterColor(wordIndex: number, letterIndex: number): string {
    const currentForeignWord = accentCleanse(homeSlice.foreignWords[wordIndex], homeSlice.strictMode)
    const currentInputWord = accentCleanse(homeSlice.inputWords[wordIndex], homeSlice.strictMode)

    const dynamicDefaultColor = homeSlice.nativeWordActive ? inactiveDefaultColor : defaultColor

    // default guard clause
    if (!currentInputWord || !currentForeignWord) return dynamicDefaultColor

    // input letter not typed yet (doesn't apply to non alphabet languages)
    if (!currentInputWord[letterIndex]) return dynamicDefaultColor

    // matching input and foreign letters
    if (currentForeignWord[letterIndex] === currentInputWord[letterIndex]) {
      return correctColor
    } else if (nonAlphabetAndNonEasyMode && wordIndex === homeSlice.wordIndex) {
      return dynamicDefaultColor
    } else {
      return wrongColor
    }
  }

  // default color different shade of gray depending on native word active or not. also when not in mixed mode it's never inactive
  function decideNativeLetterColor(wordIndex: number, letterIndex: number) {
    const currentNativeWord = accentCleanse(homeSlice.nativeWords[wordIndex], homeSlice.strictMode)
    const currentNativeInputWord = accentCleanse(homeSlice.nativeInputWords[wordIndex], homeSlice.strictMode)

    const dynamicDefaultColor = homeSlice.nativeWordActive || !homeSlice.mixedMode ? defaultColor : inactiveDefaultColor

    // default guard clause
    if (!currentNativeInputWord || !currentNativeWord) return dynamicDefaultColor

    // input letter not typed yet
    if (!currentNativeInputWord[letterIndex]) return dynamicDefaultColor

    // matching input and foreign letters
    if (currentNativeWord[letterIndex] === currentNativeInputWord[letterIndex]) {
      return correctColor
    } else if (nativeNotAlphabet && wordIndex === homeSlice.wordIndex) {
      return dynamicDefaultColor
    } else {
      return wrongColor
    }
  }

  // useful in case of input word being longer than foreign word
  function getInputOrForeignWord() {
    // default guard clause
    if (!homeSlice.foreignWords[wordIndex] || !homeSlice.inputWords[wordIndex]) return homeSlice.foreignWords[wordIndex]

    // japanese guard clause
    // if (homeSlice.foreignLanguage === "ja") return homeSlice.foreignWords[wordIndex] // TODO: might have to add chinese and korean here (and arabic)

    // if input word is longer than foreign word, extend the foreign word
    if (homeSlice.inputWords[wordIndex].length > homeSlice.foreignWords[wordIndex].length) {
      return (
        homeSlice.foreignWords[wordIndex] +
        homeSlice.inputWords[wordIndex].slice(homeSlice.foreignWords[wordIndex].length)
      )
    } else {
      return homeSlice.foreignWords[wordIndex]
    }
  }

  function determineCursorActivity(letterIndex: number) {
    // wrong word index
    if (homeSlice.wordIndex !== wordIndex) return "hidden"

    // word cursor instead of letter cursor for composed languages
    if (nonAlphabetAndNonEasyMode) return "hidden"

    // first letter of test
    if (homeSlice.inputWords.length === 0 && wordIndex === 0 && letterIndex === 0) return ""

    // guard clause
    if (homeSlice.inputWords[wordIndex] === undefined) return "hidden"
    // if (homeSlice.wordIndex !== wordIndex || homeSlice.inputWords[wordIndex] === undefined) return "hidden"

    // first letter of word
    if (homeSlice.inputWords[wordIndex].length === 0 && letterIndex === 0) return ""

    // all other letters
    if (homeSlice.inputWords[wordIndex].length === letterIndex) return ""

    return "hidden"
  }

  function determineCursorActivitySpace() {
    // don't show space cursor if language is composed
    if (nonAlphabetAndNonEasyMode) return "hidden"

    // hide if native word is active
    if (homeSlice.nativeWordActive) return "hidden"

    // wrong word index
    // if (homeSlice.wordIndex < wordIndex) return "hidden"
    if (homeSlice.wordIndex !== wordIndex) return "hidden"

    // guard clause
    if (homeSlice.inputWords[wordIndex] === undefined || homeSlice.foreignWords[wordIndex] === undefined)
      return "hidden"

    // correct word index and word is finished
    if (homeSlice.inputWords[wordIndex].length >= homeSlice.foreignWords[wordIndex].length) return "" // guard clause

    return "hidden"
  }

  function determineCursorActivityWord() {
    if (homeSlice.wordIndex !== wordIndex) return "hidden"
    // whole word not hidden if foreign language is non alphabetic
    if (nonAlphabetAndNonEasyMode) return ""
    return "hidden"
  }

  function determineCursorActivityNative(letterIndex: number) {
    // wrong word index
    if (homeSlice.wordIndex !== wordIndex) return "hidden"

    // word cursor instead of letter cursor for composed languages
    if (nativeNotAlphabet) return "hidden"

    // native word inactive
    if (!homeSlice.nativeWordActive) return "hidden"

    // first letter of test
    // if (homeSlice.inputWords.length === 0 && wordIndex === 0 && letterIndex === 0) return ""

    // guard clause
    if (homeSlice.nativeInputWords[wordIndex] === undefined) return "hidden"
    // if (homeSlice.wordIndex !== wordIndex || homeSlice.inputWords[wordIndex] === undefined) return "hidden"

    // first letter of word
    if (homeSlice.nativeInputWords[wordIndex].length === 0 && letterIndex === 0) return ""

    // all other letters
    if (homeSlice.nativeInputWords[wordIndex].length === letterIndex) return ""

    return "hidden"
  }

  function determineCursorActivityNativeWord() {
    // wrong word index
    if (homeSlice.wordIndex !== wordIndex) return "hidden"

    // native word inactive
    if (!homeSlice.nativeWordActive) return "hidden"

    // whole word not hidden if foreign language is non alphabetic
    if (nativeNotAlphabet) return ""

    return "hidden"
  }

  // makes sure presstrip number of words don't get rendered to simulate line skip
  function calcLineSkip() {
    if (homeSlice.overflowedLines.length < 2) return ""

    // if (homeSlice.overflowedLines[homeSlice.overflowedLines.length - 2] >= wordIndex) {
    if ((homeSlice.overflowedLines.at(-2) ?? 0) >= wordIndex) {
      return "hidden"
    } else {
      return ""
    }
  }

  return (
    <div className={calcLineSkip()}>
      {/* native */}
      {showNativeWord && ( // conditional native word rendering
        <>
          <div
            //   className={`absolute bg-gray-600 p-1 ${nonAlphabetLanguages.includes(homeSlice.foreignLanguage) ? "z-20" : ""} rounded-md border-2 -my-11 ${homeSlice.nativeWordActive ? "border-2 border-gray-100" : "border-transparent"}`}
            className={`${conditionalWordBase} border-2 -my-11.5 ${homeSlice.nativeWordActive ? "border-gray-100" : "border-transparent"}`}
          >
            {/* word cursor background */}
            <div className={`text-transparent ${cursorBaseNative} ${determineCursorActivityNativeWord()}`}>
              {homeSlice.nativeWords[wordIndex]}
            </div>
            {homeSlice.nativeWords[wordIndex].split("").map((letter, letterIndex) => (
              <div className="inline-block" key={letterIndex}>
                {/* letter cursor background */}
                <div className={`text-transparent ${cursorBaseNative} ${determineCursorActivityNative(letterIndex)}`}>
                  {letter}
                </div>
                {/* letter (relative to make z-index work) */}
                <span
                  className={`relative ${decideNativeLetterColor(wordIndex, letterIndex)} ${decideAnimation(true)}`}
                >
                  {letter /** .replace("_", " ") */}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {/* foreign */}
      <div ref={ref}>
        {/* word cursor background */}
        <div className={`text-transparent ${cursorBase} ${determineCursorActivityWord()}`}>
          {getInputOrForeignWord()}
        </div>
        {/* word */}
        {getInputOrForeignWord()
          .split("")
          .map((letter, letterIndex) => {
            return (
              <div className="inline-block" key={letterIndex}>
                {/* letter cursor background */}
                <div className={`text-transparent ${cursorBase} ${determineCursorActivity(letterIndex)}`}>{letter}</div>
                {/* letter (relative to make z-index work) */}
                <span className={`leading-loose relative ${decideLetterColor(wordIndex, letterIndex)}`}>{letter}</span>
              </div>
            )
          })}
        {/* space cursor background */}
        <span className={`${cursorBase} ${determineCursorActivitySpace()}`}>&nbsp;</span>
        {/* space */}
        <span>&nbsp;</span>
      </div>
      {/* secondary spelling */}
      {showNativeWord && nonAlphabetLanguages.includes(homeSlice.foreignLanguage) && (
        <div className={`${conditionalWordBase} -my-1.5 ${defaultColor}`}>
          <p className={decideAnimation(false)}>{homeSlice.romanizedWords[homeSlice.wordIndex]}</p>
        </div>
      )}
    </div>
  )
}
